import {
  CandidateRow,
  ElementOriginMapping,
  ProjectElementImport,
} from '@shared/models/element-import.model';
import { ElementCategoryID } from '@shared/models/element-categories.model';
import {
  ElementQuantityExpressionName,
  elementQuantityUnitRecord,
} from '@shared/models/element-quantities.model';
import { ExpressionValue, IElement } from '@shared/models/project.model';
import { IProduct } from '@shared/models/product.model';
import { Recipe } from '@shared/models/recipe.model';
import {
  candidateName,
  tryMaterializeCandidateRow,
} from '@shared/helpers/materialize-candidate.helpers';
import type { IfcMeasure } from '@shared/helpers/ifc-quantity.helpers';
import { getSelectedElementQuantityProperty } from '@shared/helpers/element-quantity-core.helpers';
import { getQuantityDisplayUnitForEdit } from '@shared/helpers/element-quantity.helpers';
import { WasteScale } from '@shared/helpers/waste-scale.helpers';
import { getElementIconCategoryName } from '@features/elements/helpers/get-element-category-icon-name.helper';
import { getElementListRowDisplay } from '@features/proposals/ElementListRow/element-list-row-display.helpers';
import { NodonIcons } from '@ui/Icon/Icon';
import { MappingsByCode } from './element-imports.store';
import { ProductsById, RecipesById } from './candidate-status.helpers';

export type CandidateElementDisplay = {
  iconName: keyof NodonIcons['elementCategory'];
  name: string;
  quantityValue: string | undefined;
  unit: string;
  /**
   * The selected quantity's real {@link ExpressionValue} from the materialized
   * (or ghost) element — carries the `"<qty> * <factor>"` formula, so the fx
   * panel seeds the expression instead of a bare resolved number.
   */
  quantityExpression: ExpressionValue | undefined;
  /** True when the row is materializable but not yet imported. */
  ghost: boolean;
};

export type CandidateRowDisplayContext = {
  mappingsByCode: MappingsByCode;
  productsById: ProductsById;
  /** Recipes keyed by id, for recipe-mapped rows (DEV-3387). */
  recipesById?: RecipesById;
  importsById: Record<string, ProjectElementImport | undefined>;
  /** Per-row inline mapping overlay (unsaved draft) for the previewed row. */
  draftMapping?: ElementOriginMapping;
  /** Element quantity the row is mapped to (unsaved draft), pre-filled in the preview. */
  selectedQuantity?: ElementQuantityExpressionName;
  /** Explicit Quantity from file pick (DEV-3577). */
  ifcMeasure?: IfcMeasure;
};

const buildGhostElement = (
  row: CandidateRow,
  ctx: CandidateRowDisplayContext,
): IElement | undefined => {
  const result = tryMaterializeCandidateRow(row, {
    mappingsByCode: ctx.mappingsByCode,
    productsById: ctx.productsById,
    recipesById: ctx.recipesById,
    importsById: ctx.importsById,
    draftMappingsByRowId: ctx.draftMapping
      ? { [row.id]: ctx.draftMapping }
      : undefined,
    selectedQuantityByRowId: ctx.selectedQuantity
      ? { [row.id]: ctx.selectedQuantity }
      : undefined,
    ifcMeasureByRowId: ctx.ifcMeasure
      ? { [row.id]: ctx.ifcMeasure }
      : undefined,
  });
  return result.ok ? result.element : undefined;
};

/**
 * Element-list display for a candidate row with a resolvable product / element.
 * Uses the live project element when imported; otherwise the materialize baseline.
 */
export const getCandidateRowElementDisplay = (params: {
  row: CandidateRow;
  product: IProduct | undefined;
  /** Recipe the row maps to (DEV-3387); drives the ghost when no product. */
  recipe?: Recipe | undefined;
  mappings: MappingsByCode;
  importedElement: IElement | undefined;
  wasteScale: WasteScale | undefined;
  productsById?: ProductsById;
  recipesById?: RecipesById;
  importsById?: CandidateRowDisplayContext['importsById'];
  /** Per-row inline mapping overlay (unsaved draft) driving the ghost preview. */
  draftMapping?: ElementOriginMapping;
  /** Element quantity the row is mapped to (unsaved draft), pre-filled in the preview. */
  selectedQuantity?: ElementQuantityExpressionName;
  ifcMeasure?: IfcMeasure;
}): CandidateElementDisplay | undefined => {
  const {
    row,
    product,
    recipe,
    mappings,
    importedElement,
    wasteScale,
    productsById = product ? { [product.id]: product } : {},
    recipesById = recipe ? { [recipe.id]: recipe } : {},
    importsById = {},
    draftMapping,
    selectedQuantity,
    ifcMeasure,
  } = params;

  const element =
    importedElement ??
    (product || recipe
      ? buildGhostElement(row, {
          mappingsByCode: mappings,
          productsById,
          recipesById,
          importsById:
            Object.keys(importsById).length > 0
              ? importsById
              : ({
                  [row.importId]: {
                    metadata: { interpretations: { wasteScale } },
                  },
                } as Record<string, ProjectElementImport | undefined>),
          draftMapping,
          selectedQuantity,
          ifcMeasure,
        })
      : undefined);
  if (!element) return undefined;

  const categoryId = element.category_id ?? ElementCategoryID.Other;
  const iconName = getElementIconCategoryName(categoryId);
  const name = element.name || candidateName(row) || '';

  const selectedProp = getSelectedElementQuantityProperty(element);
  const selectedName = (selectedProp?.name ?? element.selectedQuantity) as
    ElementQuantityExpressionName | undefined;
  const defaultUnit = selectedName
    ? (elementQuantityUnitRecord[selectedName] ?? '')
    : '';
  const typedValue =
    selectedProp?.count?.resolved ?? selectedProp?.count?.value;
  const display = getElementListRowDisplay({
    typedValue,
    typedUnit: selectedProp?.unit,
    customDisplayUnit: getQuantityDisplayUnitForEdit(element, selectedName),
    defaultUnit,
    fallbackUnit: undefined,
    fallbackPlaceholder: '',
  });

  return {
    iconName,
    name,
    quantityValue: display.displayValue,
    unit: display.displayUnit,
    quantityExpression: selectedProp?.count,
    ghost: !importedElement,
  };
};
